import type { AutoUncleRatingIntervals } from "@/types/valuation";
import type { PriceCategory } from "@/types/vehicleCard";

const labelToDots = (label: string): number => {
  const labelLower = label.toLowerCase();
  // Swedish labels
  if (labelLower.includes("mycket bra")) return 5;
  if (labelLower.includes("bra pris")) return 4;
  if (labelLower.includes("rimligt")) return 3;
  if (labelLower.includes("något dyrt")) return 2;
  if (labelLower === "dyrt") return 1;
  // English labels (fallback)
  if (labelLower.includes("super")) return 5;
  if (labelLower.includes("good")) return 4;
  if (labelLower.includes("fair")) return 3;
  if (labelLower.includes("bit expensive") || labelLower.includes("a bit")) return 2;
  if (labelLower.includes("expensive")) return 1;
  return 3;
};

const labelToColor = (
  label: string
): "success" | "info" | "warning" | "destructive" => {
  const labelLower = label.toLowerCase();
  // Swedish labels
  if (labelLower.includes("mycket bra")) return "success";
  if (labelLower.includes("bra pris")) return "info";
  if (labelLower.includes("rimligt")) return "warning";
  if (labelLower.includes("något dyrt")) return "warning";
  if (labelLower === "dyrt") return "destructive";
  // English labels (fallback)
  if (labelLower.includes("super")) return "success";
  if (labelLower.includes("good")) return "info";
  if (labelLower.includes("fair")) return "warning";
  if (labelLower.includes("bit expensive") || labelLower.includes("a bit")) return "warning";
  if (labelLower.includes("expensive")) return "destructive";
  return "warning";
};

/** Map API price-interval label to i18n key (auctions.priceIntervals.*) */
export const getPriceIntervalI18nKey = (label: string): string | null => {
  const labelLower = label.toLowerCase();
  if (labelLower.includes("mycket bra") || labelLower.includes("very good")) return "veryGoodPrice";
  if (labelLower.includes("bra pris") || labelLower.includes("good price")) return "goodPrice";
  if (labelLower.includes("rimligt") || labelLower.includes("fair")) return "fairPrice";
  if (labelLower.includes("något dyrt") || labelLower.includes("somewhat expensive") || labelLower.includes("a bit")) return "somewhatExpensive";
  if (labelLower === "dyrt" || labelLower === "expensive") return "expensive";
  return null;
};

export const mapRatingIntervalsToCategories = (
  intervals?: AutoUncleRatingIntervals
): PriceCategory[] => {
  if (!intervals) return [];

  return Object.values(intervals)
    .map((interval) => ({
      label: interval.label,
      lowerBound: interval.lower_bound,
      upperBound: interval.upper_bound,
      days: interval.typical_sale_time !== null ? `${interval.typical_sale_time} dagar` : "",
      dots: labelToDots(interval.label),
      color: labelToColor(interval.label),
    }))
    .sort((a, b) => b.lowerBound - a.lowerBound);
};
